import { put, all, call, takeLeading, takeLatest } from 'redux-saga/effects';
import types from './actionTypes';
import * as weeklyPushNotificationsActions from './actions';
import {
  getFirestore,
  collection,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import rsf from '../../../helpers/firebase';
import { uploadFileToS3 } from '../../../helpers/file';
import toastr from 'toastr';
import { toDateFirebase } from '../../../helpers/sharedFunction';

const STORAGE_BASE_PATH = 'weeklyPushNotifications';
const db = getFirestore(rsf.app);

export const weeklyPushNotificationTransformer = (weeklyPushNotification) => {
  const data = weeklyPushNotification.data();
  return {
    id: weeklyPushNotification.id,
    ...data,
    ...(data.createdAt && {
      createdAt: toDateFirebase(weeklyPushNotification, data).toDate(),
    }),
    ...(data.updatedAt && {
      updatedAt: toDateFirebase(
        weeklyPushNotification,
        data,
        'updatedAt',
      ).toDate(),
    }),
  };
};

function* createWeeklyPushNotificationSaga({ weeklyPushNotification }) {
  try {
    const weeklyPushNotificationsRef = collection(
      db,
      'weeklyPushNotifications',
    );
    const id = weeklyPushNotificationsRef.id;

    let imageURL;
    if (weeklyPushNotification.files) {
      const { image } = weeklyPushNotification.files;
      [imageURL] = yield all(
        Object.values({ image }).map((file) => {
          const path = `${STORAGE_BASE_PATH}/${file.path}`;
          return call(uploadFileToS3, file, path);
        }),
      );
    }

    delete weeklyPushNotification.files;

    const newWeeklyPushNotification = {
      ...weeklyPushNotification,
      active: true,
      ...(imageURL && { image: imageURL }),
    };
    yield call(rsf.firestore.addDocument, weeklyPushNotificationsRef, {
      ...newWeeklyPushNotification,
      createdAt: serverTimestamp(),
    });
    yield put(
      weeklyPushNotificationsActions.createWeeklyPushNotificationSuccess({
        id,
        ...newWeeklyPushNotification,
        createdAt: new Date(),
      }),
    );
    toastr.success('Weekly Push Notification created!', '');
  } catch (error) {
    yield put(
      weeklyPushNotificationsActions.createWeeklyPushNotificationFailure(error),
    );
    toastr.error(error.message);
  }
}

function* updateWeeklyPushNotificationSaga({ weeklyPushNotification }) {
  try {
    const { id, files } = weeklyPushNotification;
    const weeklyPushNotificationRef = doc(db, 'weeklyPushNotifications', id);

    let imageURL;
    if (files?.image) {
      const path = `${STORAGE_BASE_PATH}/${files.image.path}`;
      imageURL = yield call(uploadFileToS3, files.image, path);
    }

    delete weeklyPushNotification.id;
    delete weeklyPushNotification.files;
    delete weeklyPushNotification.createdAt;

    const newWeeklyPushNotification = {
      ...weeklyPushNotification,
      ...(imageURL && { image: imageURL }),
    };

    yield call(
      rsf.firestore.setDocument,
      weeklyPushNotificationRef,
      {
        ...newWeeklyPushNotification,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    yield put(
      weeklyPushNotificationsActions.updateWeeklyPushNotificationSuccess({
        id,
        ...newWeeklyPushNotification,
      }),
    );
    toastr.success('Weekly Push Notification updated!', '');
  } catch (error) {
    yield put(
      weeklyPushNotificationsActions.updateWeeklyPushNotificationFailure(error),
    );
    toastr.error(error.message);
  }
}

function* fetchWeeklyPushNotificationsSaga({ filters }) {
  try {
    const weeklyPushNotificationsRef = collection(
      db,
      'weeklyPushNotifications',
    );
    const weeklyPushNotificationsSnap = yield call(
      rsf.firestore.getCollection,
      weeklyPushNotificationsRef,
    );
    const weeklyPushNotifications = weeklyPushNotificationsSnap.docs.map(
      (weeklyPushNotification) =>
        weeklyPushNotificationTransformer(weeklyPushNotification),
    );
    yield put(
      weeklyPushNotificationsActions.fetchWeeklyPushNotificationsSuccess(
        weeklyPushNotifications,
        filters,
      ),
    );
  } catch (error) {
    yield put(
      weeklyPushNotificationsActions.fetchWeeklyPushNotificationsFailure(error),
    );
  }
}

function* weeklyPushNotificationSaga() {
  yield all([
    takeLatest(
      types.FETCH_WEEKLY_PUSH_NOTIFICATIONS.REQUEST,
      fetchWeeklyPushNotificationsSaga,
    ),
    takeLeading(
      types.CREATE_WEEKLY_PUSH_NOTIFICATION.REQUEST,
      createWeeklyPushNotificationSaga,
    ),
    takeLeading(
      types.UPDATE_WEEKLY_PUSH_NOTIFICATION.REQUEST,
      updateWeeklyPushNotificationSaga,
    ),
  ]);
}

export default weeklyPushNotificationSaga;
